import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useRole } from "../lib/roleContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ChevronLeft, ShieldCheck, Lock, Check, X, Wallet, FileCheck2, Info, ArrowRight, Ban, ShoppingBag, UserRound, Users } from "lucide-react";

const STATE_META = {
  eligible:                            { label: "Eligible",                    cls: "bg-[#E8F1EA] text-[#2E5B3D] border-[#B7D1BD]", icon: Check },
  parent_approval_required:            { label: "Parent approval required",    cls: "bg-[#FFF4E2] text-[#7A4A00] border-[#E9CC9A]", icon: UserRound },
  officer_approval_required:           { label: "Officer approval required",   cls: "bg-[#FFF4E2] text-[#7A4A00] border-[#E9CC9A]", icon: ShieldCheck },
  officer_and_parent_approval_required:{ label: "Officer + Parent approval",   cls: "bg-[#FFF4E2] text-[#7A4A00] border-[#E9CC9A]", icon: Users },
  not_eligible:                        { label: "Not eligible yet",            cls: "bg-[#F4F5F6] text-[#555] border-[#D5D7DA]",    icon: Info },
  blocked:                             { label: "Restricted for junior",       cls: "bg-[#FBE9E7] text-[#7A2E16] border-[#E9B4AA]", icon: Ban },
};

export default function DriverOffers() {
  const { id } = useParams();
  const { role } = useRole();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [claimOpen, setClaimOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [personalised, setPersonalised] = useState(false);

  const refresh = () => api.driverOffers(id).then(setData);
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  if (!data) return <Card className="p-8 text-sm text-muted-foreground">Loading offers…</Card>;

  const openClaim = (offer) => { setActive(offer); setPersonalised(false); setClaimOpen(true); };

  const submitClaim = async () => {
    try {
      const c = await api.claimOffer(active.id, {
        driver_id: id,
        personalised,
        requested_by: data.driver.name,
      });
      toast.success("Request raised", {
        description: c.status === "claim_recorded"
          ? `Claim ${c.id} recorded.`
          : `Awaiting ${c.status.replace("pending_", "").replace("_", " + ")} approval.`,
      });
      setClaimOpen(false);
      nav(`/offer-claims/${c.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Request failed");
    }
  };

  return (
    <div className="space-y-8" data-testid="driver-offers-page">
      <Link to={`/drivers/${id}`} className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-driver">
        <ChevronLeft className="h-4 w-4" /> Back to {data.driver.name}'s profile
      </Link>

      {/* Header */}
      <section className="relative overflow-hidden rounded-2xl border border-border bg-white">
        <div className="p-6 sm:p-8 grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3">
            <div className="flex items-center gap-2">
              <span className="signal-bar" />
              <span className="tracking-eyebrow text-muted-foreground">Bounded service layer · Secondary demo</span>
            </div>
            <h1 className="text-3xl sm:text-4xl tracking-tight font-semibold mt-2">Eligible support and kit</h1>
            <p className="text-sm text-muted-foreground mt-3 max-w-2xl leading-relaxed">
              The club shop is not just a catalogue. Some items, offers or support packages depend on member role,
              age band, sponsor eligibility, parental permission or supplier terms. ClubCierge verifies those
              conditions before an offer can be shown, claimed or recorded.
            </p>
            <div className="mt-4 text-[12px] text-muted-foreground font-mono-ui leading-relaxed">
              AI suggests. Wallets prove eligibility. Parents and officers approve where required. KATLAS records
              the governed interaction.
            </div>
          </div>
          <Card className="lg:col-span-2 p-5 border-border bg-[#FAFAFB] space-y-3" data-testid="wallet-card">
            <div className="flex items-center gap-2 tracking-eyebrow text-muted-foreground">
              <Wallet className="h-3.5 w-3.5" /> Member wallet — proofs available
            </div>
            <ul className="text-xs space-y-1.5">
              <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[var(--rag-green-text)]" /> Member role: <span className="font-mono-ui">Driver</span></li>
              <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[var(--rag-green-text)]" /> Age band: <span className="font-mono-ui">{data.driver.age_band}</span></li>
              <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[var(--rag-green-text)]" /> Club affiliation: <span className="font-mono-ui">Apex Junior Academy</span></li>
              <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[var(--rag-green-text)]" /> Sponsor-readiness: <span className="font-mono-ui">{data.driver.id === "d-marcus" ? "ready" : "pending"}</span></li>
              {data.offers.some(o => o.evaluation?.approved_intro_ref) && (
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[var(--rag-green-text)]" /> Approved sponsor introduction on file</li>
              )}
              {data.driver.is_junior && (
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[var(--rag-green-text)]" /> Parent/guardian wallet linked</li>
              )}
            </ul>
            <div className="text-[11px] text-muted-foreground border-t border-border pt-2">
              The wallet proves eligibility without exposing more personal data than needed.
            </div>
          </Card>
        </div>
      </section>

      {/* Offers grid */}
      <section>
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="tracking-eyebrow text-muted-foreground">Offers</div>
            <h2 className="text-xl font-semibold mt-0.5">Available to {data.driver.name}</h2>
          </div>
          <span className="text-xs font-mono-ui text-muted-foreground">{data.offers.length} offers · {data.offers.filter(o => o.evaluation.state === "eligible").length} eligible now</span>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {data.offers.map((o) => (
            <OfferCard key={o.id} offer={o} driver={data.driver} role={role} onClaim={openClaim} />
          ))}
        </div>
      </section>

      <Card className="p-5 border-border bg-[#FAFAFB] race-card" data-testid="katlas-note">
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-[#4A5D4E] shrink-0 mt-0.5" />
          <div className="text-sm leading-relaxed text-muted-foreground">
            <span className="text-foreground font-medium">KATLAS governs the permissioned claim.</span> It does not
            process payment or expose private wallet contents. The receipt records what was checked, what was
            shared and what was withheld.
          </div>
        </div>
      </Card>

      <Dialog open={claimOpen} onOpenChange={setClaimOpen}>
        <DialogContent data-testid="claim-dialog">
          <DialogHeader>
            <DialogTitle>Request: {active?.name}</DialogTitle>
            <DialogDescription>
              The wallet will share <b>{(active?.shared_fields || []).join(", ") || "no fields"}</b> with the
              supplier. <b>{(active?.withheld_fields || []).join(", ")}</b> stays withheld.
            </DialogDescription>
          </DialogHeader>
          {active?.id === "o-prova-hoodie" && data.driver.is_junior && (
            <div className="flex items-start gap-2 rounded-md border border-border p-3 bg-[#FFFCF5]">
              <Checkbox id="personalised" checked={personalised} onCheckedChange={setPersonalised} data-testid="personalised-checkbox" />
              <Label htmlFor="personalised" className="text-sm leading-relaxed">
                Personalise with name &amp; size — <span className="text-muted-foreground">if ticked, parent/guardian approval will be required before contact / sizing details are shared with the supplier.</span>
              </Label>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setClaimOpen(false)}>Cancel</Button>
            <Button onClick={submitClaim} data-testid="submit-claim">Submit request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OfferCard({ offer, driver, role, onClaim }) {
  const ev = offer.evaluation;
  const meta = STATE_META[ev.state] || STATE_META.not_eligible;
  const Icon = meta.icon;
  const recorded = offer.recorded_claim;
  const canRequest = ev.state !== "blocked" && ev.state !== "not_eligible";
  return (
    <Card className="p-5 border-border race-card flex flex-col gap-3" data-testid={`offer-card-${offer.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] tracking-eyebrow text-muted-foreground">{offer.type_label}</div>
          <h3 className="text-[17px] font-semibold mt-0.5">{offer.name}</h3>
          <div className="text-[11px] text-muted-foreground font-mono-ui mt-0.5">{offer.supplier}</div>
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] tracking-eyebrow font-mono-ui px-2 py-0.5 rounded-full border ${meta.cls}`} data-testid={`offer-state-${offer.id}`}>
          <Icon className="h-3 w-3" /> {meta.label}
        </span>
      </div>

      <p className="text-sm leading-relaxed text-muted-foreground">{offer.summary}</p>

      <div className="grid grid-cols-2 gap-3 text-[11px] border-t border-border pt-3">
        <div>
          <div className="tracking-eyebrow text-muted-foreground">Shared with supplier</div>
          <ul className="mt-1 space-y-0.5">
            {(offer.shared_fields || []).map((s) => <li key={s} className="flex gap-1.5"><Check className="h-3 w-3 mt-0.5 text-[var(--rag-green-text)]" /><span>{s}</span></li>)}
            {(offer.shared_fields || []).length === 0 && <li className="text-muted-foreground italic">No personal data shared</li>}
          </ul>
        </div>
        <div>
          <div className="tracking-eyebrow text-muted-foreground">Withheld</div>
          <ul className="mt-1 space-y-0.5">
            {(offer.withheld_fields || []).map((s) => <li key={s} className="flex gap-1.5"><Lock className="h-3 w-3 mt-0.5 text-muted-foreground" /><span>{s}</span></li>)}
          </ul>
        </div>
      </div>

      {offer.explanation?.why && (
        <div className="text-[12px] leading-relaxed text-muted-foreground border-l-2 border-[#F25E1D] pl-2">
          {offer.explanation.why}
        </div>
      )}

      {(ev.reasons_fail || []).length > 0 && (
        <ul className="text-[12px] text-muted-foreground space-y-0.5">
          {ev.reasons_fail.map((r, i) => <li key={i} className="flex gap-1.5"><X className="h-3 w-3 mt-0.5 text-[var(--rag-red-text)]" /><span>{r}</span></li>)}
        </ul>
      )}

      <div className="flex items-center justify-between border-t border-border pt-3 mt-1">
        <div className="text-[11px] text-muted-foreground font-mono-ui">
          {ev.approved_intro_ref && <>Linked introduction: {ev.approved_intro_ref}</>}
        </div>
        {recorded ? (
          <Link to={`/offer-claims/${recorded.id}`}>
            <Button variant="outline" size="sm" data-testid={`open-claim-${offer.id}`}>
              <FileCheck2 className="h-3.5 w-3.5 mr-1" />
              {recorded.status === "claim_recorded" ? "View claim receipt" : "View pending claim"}
            </Button>
          </Link>
        ) : (
          <Button
            size="sm"
            disabled={!canRequest}
            onClick={() => onClaim(offer)}
            data-testid={`claim-${offer.id}`}
            variant={ev.state === "eligible" ? "default" : "outline"}
          >
            {ev.state === "blocked" ? <><Ban className="h-3.5 w-3.5 mr-1" /> Hidden in production</> :
             ev.state === "not_eligible" ? <>Not available <ArrowRight className="h-3.5 w-3.5 ml-1" /></> :
             <><ShoppingBag className="h-3.5 w-3.5 mr-1" /> Request</>}
          </Button>
        )}
      </div>
    </Card>
  );
}
